import React, { useState } from 'react';
import { useStudioColumnVisibility } from 'src/components/star-grid/hooks/useStudioColumnVisibility';
import { STUDIO_COLUMN_REORDER_TYPE } from 'src/components/star-grid/reportStudioConstants';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly index: number;
  readonly label: string;
  readonly draggable?: boolean;
  readonly children: React.ReactNode;
  readonly onDrop: React.DragEventHandler<HTMLDivElement>;
}
// Selector for the interactive controls inside a card: a drag must NOT start on these, so their
// clicks, selection and keyboard still work -- the rest of the card is the drag surface.
const CONTROL_SELECTOR = 'input, select, textarea, button, a, [role="button"], [role="listbox"], [role="combobox"], [contenteditable="true"]';
export const StudioColumnSlot: React.FC<Props> = ({ index, label, draggable = false, children, onDrop }) => {
  const state = useStudioColumnVisibility(index);
  const [dragging, setDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState(false);
  const isReorderDrag = (event: React.DragEvent) => event.dataTransfer.types.includes(STUDIO_COLUMN_REORDER_TYPE);
  const className = [styles.blockWrap, dragging && styles.dragging, dropTarget && styles.dropTarget].filter(Boolean).join(' ');
  return (
    <div
      ref={state.element}
      className={className}
      data-testid="studio-column-slot"
      tabIndex={state.ready ? undefined : 0}
      aria-label={label}
      role="group"
      draggable={draggable}
      title={draggable ? 'Drag to reorder' : undefined}
      onFocus={state.reveal}
      onDragStart={
        draggable
          ? (event) => {
              // Grab anywhere on the card EXCEPT its controls, so reordering feels native while the
              // scenario/version pickers and buttons keep working.
              if ((event.target as HTMLElement).closest(CONTROL_SELECTOR)) {
                event.preventDefault();
                return;
              }
              event.dataTransfer.setData(STUDIO_COLUMN_REORDER_TYPE, String(index));
              event.dataTransfer.effectAllowed = 'move';
              setDragging(true);
            }
          : undefined
      }
      onDragEnd={() => {
        setDragging(false);
        setDropTarget(false);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (isReorderDrag(event)) setDropTarget(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropTarget(false);
      }}
      onDrop={(event) => {
        setDropTarget(false);
        onDrop(event);
      }}
    >
      {state.ready ? children : <div className={styles.columnPlaceholder}>{label}</div>}
    </div>
  );
};
